import React, { useState, useEffect } from 'react';
import { 
  BadgePercent, Shield, Globe, Building2, MapPin, ExternalLink, 
  CheckCircle2, AlertTriangle, Clock, History, Edit3, Save, X,
  ChevronDown, ChevronRight, RefreshCw, Loader2, Check, FileText,
  Eye, RotateCcw, Link2, Calendar, User, Plus, Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TaxTableManager = ({ companyId = 'default' }) => {
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [taxTables, setTaxTables] = useState({ federal: [], state: [], local: [] });
  const [stats, setStats] = useState({ total: 0, verified: 0, pending_review: 0, unverified: 0 });
  const [authorities, setAuthorities] = useState([]);
  
  // View state
  const [activeView, setActiveView] = useState('overview'); // 'overview', 'federal', 'state', 'local', 'audit'
  const [selectedTable, setSelectedTable] = useState(null);
  const [expandedStates, setExpandedStates] = useState({});
  
  // Edit state
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editReason, setEditReason] = useState('');
  const [saving, setSaving] = useState(false);
  
  // History state
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  
  // Audit log state
  const [auditLog, setAuditLog] = useState([]);

  useEffect(() => {
    fetchTaxTables();
    fetchAuthorities();
  }, [companyId]);

  const fetchTaxTables = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tax-tables/?company_id=${companyId}`);
      if (res.ok) {
        const data = await res.json();
        setTaxTables(data.tables);
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Error fetching tax tables:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuthorities = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tax-tables/authorities`);
      if (res.ok) {
        const data = await res.json();
        setAuthorities(data.authorities);
      }
    } catch (err) {
      console.error('Error fetching authorities:', err);
    }
  };

  const initializeTaxTables = async () => {
    setInitializing(true);
    try {
      const res = await fetch(`${API_URL}/api/tax-tables/initialize?company_id=${companyId}`);
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.tables_created} tax tables initialized`);
        fetchTaxTables();
      }
    } catch (err) {
      toast.error('Failed to initialize tax tables');
    } finally {
      setInitializing(false);
    }
  };

  const fetchTableDetails = async (jurisdictionCode, taxType = 'income') => {
    try {
      const res = await fetch(`${API_URL}/api/tax-tables/${jurisdictionCode}?tax_type=${taxType}&company_id=${companyId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedTable(data);
        setEditData(null);
        setEditMode(false);
      }
    } catch (err) {
      toast.error('Failed to load tax table details');
    }
  };

  const fetchHistory = async (jurisdictionCode, taxType = 'income') => {
    try {
      const res = await fetch(`${API_URL}/api/tax-tables/${jurisdictionCode}/history?tax_type=${taxType}&company_id=${companyId}`);
      if (res.ok) {
        const data = await res.json();
        setHistoryData(data);
        setShowHistory(true);
      }
    } catch (err) {
      toast.error('Failed to load version history');
    }
  };

  const fetchAuditLog = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tax-tables/audit/log?company_id=${companyId}&limit=100`);
      if (res.ok) {
        const data = await res.json();
        setAuditLog(data.changes);
      }
    } catch (err) {
      console.error('Error fetching audit log:', err);
    }
  };

  const handleSaveChanges = async () => {
    if (!editReason.trim()) {
      toast.error('Please provide a reason for the change');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/tax-tables/${selectedTable.table.jurisdiction_code}?tax_type=${selectedTable.table.tax_type}&company_id=${companyId}&updated_by=user`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brackets: editData.brackets,
          additional_rates: editData.additional_rates,
          effective_date: editData.effective_date,
          notes: editData.notes,
          reason: editReason
        })
      });
      
      if (res.ok) {
        toast.success('Tax table updated - marked for verification');
        setEditMode(false);
        setEditReason('');
        fetchTableDetails(selectedTable.table.jurisdiction_code, selectedTable.table.tax_type);
        fetchTaxTables();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save changes');
      }
    } catch (err) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tax-tables/${selectedTable.table.jurisdiction_code}/verify?tax_type=${selectedTable.table.tax_type}&company_id=${companyId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verified_by: 'user',
          notes: 'Manually verified against official source'
        })
      });
      
      if (res.ok) {
        toast.success('Tax table marked as verified');
        fetchTableDetails(selectedTable.table.jurisdiction_code, selectedTable.table.tax_type);
        fetchTaxTables();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) {
      toast.error('Failed to verify tax table');
    }
  };

  const handleRollback = async (versionId) => {
    if (!window.confirm('Are you sure you want to rollback to this version?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/tax-tables/${selectedTable.table.jurisdiction_code}/rollback?version_id=${versionId}&tax_type=${selectedTable.table.tax_type}&company_id=${companyId}&rolled_back_by=user&reason=Manual rollback`, {
        method: 'POST'
      });
      
      if (res.ok) {
        toast.success('Rolled back successfully');
        setShowHistory(false);
        fetchTableDetails(selectedTable.table.jurisdiction_code, selectedTable.table.tax_type);
        fetchTaxTables();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) {
      toast.error('Failed to rollback');
    }
  };

  const startEdit = () => {
    setEditData({
      brackets: selectedTable.table.current_version?.brackets || [],
      additional_rates: selectedTable.table.current_version?.additional_rates || {},
      effective_date: selectedTable.table.current_version?.effective_date || new Date().toISOString().split('T')[0],
      notes: ''
    });
    setEditMode(true);
  };

  const updateBracket = (index, field, value) => {
    const newBrackets = [...editData.brackets];
    newBrackets[index] = { ...newBrackets[index], [field]: parseFloat(value) || 0 };
    setEditData({ ...editData, brackets: newBrackets });
  };

  const addBracket = () => {
    const newBrackets = [...editData.brackets, { min_amount: 0, max_amount: null, rate: 0 }];
    setEditData({ ...editData, brackets: newBrackets });
  };

  const removeBracket = (index) => {
    const newBrackets = editData.brackets.filter((_, i) => i !== index);
    setEditData({ ...editData, brackets: newBrackets });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full"><CheckCircle2 className="w-3 h-3" /> Verified</span>;
      case 'pending_review':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full"><Clock className="w-3 h-3" /> Pending Review</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full"><AlertTriangle className="w-3 h-3" /> Unverified</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  // If no tables, show initialize option
  if (stats.total === 0) {
    return (
      <Card className="text-center py-12">
        <CardContent>
          <BadgePercent className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Tax Tables Found</h3>
          <p className="text-gray-500 mb-6">Initialize tax tables for all 50 US states, Federal taxes, and local jurisdictions.</p>
          <Button onClick={initializeTaxTables} disabled={initializing}>
            {initializing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Initializing...</> : <><Plus className="w-4 h-4 mr-2" /> Initialize Tax Tables</>}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveView('overview')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-sm text-gray-500">Total Tables</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${stats.verified > 0 ? 'bg-emerald-50 border-emerald-200' : ''}`} onClick={() => setActiveView('overview')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-emerald-600">{stats.verified}</p>
            <p className="text-sm text-gray-500">Verified</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${stats.pending_review > 0 ? 'bg-amber-50 border-amber-200' : ''}`} onClick={() => setActiveView('overview')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-600">{stats.pending_review}</p>
            <p className="text-sm text-gray-500">Pending Review</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${stats.unverified > 0 ? 'bg-red-50 border-red-200' : ''}`} onClick={() => setActiveView('overview')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-600">{stats.unverified}</p>
            <p className="text-sm text-gray-500">Unverified</p>
          </CardContent>
        </Card>
      </div>

      {/* Warning Banner */}
      {stats.unverified > 0 && (
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            <div className="flex-1">
              <p className="font-medium text-red-800">Unverified Tax Tables</p>
              <p className="text-sm text-red-600">{stats.unverified} tables require manual verification before use in payroll calculations.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {[
          { id: 'overview', label: 'Overview', icon: Globe },
          { id: 'federal', label: 'Federal', icon: Building2, count: taxTables.federal?.length || 0 },
          { id: 'state', label: 'State', icon: MapPin, count: taxTables.state?.length || 0 },
          { id: 'local', label: 'Local', icon: MapPin, count: taxTables.local?.length || 0 },
          { id: 'audit', label: 'Audit Log', icon: History },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveView(tab.id); if (tab.id === 'audit') fetchAuditLog(); setSelectedTable(null); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeView === tab.id ? 'bg-sky-100 text-sky-700' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.count !== undefined && <span className="ml-1 text-xs bg-gray-200 px-1.5 py-0.5 rounded">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="grid grid-cols-3 gap-4">
        {/* Left Panel - List */}
        <div className="col-span-1 space-y-2 max-h-[600px] overflow-y-auto">
          {activeView === 'overview' && (
            <>
              <p className="text-sm font-medium text-gray-500 px-2">Quick Access</p>
              <div className="space-y-1">
                {taxTables.federal?.map(table => (
                  <button
                    key={table.id}
                    onClick={() => fetchTableDetails(table.jurisdiction_code, table.tax_type)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      selectedTable?.table?.id === table.id ? 'border-sky-500 bg-sky-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{table.tax_name}</span>
                      {getStatusBadge(table.verification_status)}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {activeView === 'federal' && (
            <div className="space-y-1">
              {taxTables.federal?.map(table => (
                <button
                  key={table.id}
                  onClick={() => fetchTableDetails(table.jurisdiction_code, table.tax_type)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    selectedTable?.table?.id === table.id ? 'border-sky-500 bg-sky-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm">{table.tax_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{table.source_name}</span>
                    {getStatusBadge(table.verification_status)}
                  </div>
                </button>
              ))}
            </div>
          )}

          {activeView === 'state' && (
            <div className="space-y-1">
              {taxTables.state?.map(table => (
                <button
                  key={table.id}
                  onClick={() => fetchTableDetails(table.jurisdiction_code, table.tax_type)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    selectedTable?.table?.id === table.id ? 'border-sky-500 bg-sky-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm">{table.jurisdiction_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{table.current_version?.additional_rates?.type === 'none' ? 'No State Tax' : `${((table.current_version?.additional_rates?.top_rate || 0) * 100).toFixed(2)}%`}</span>
                    {getStatusBadge(table.verification_status)}
                  </div>
                </button>
              ))}
            </div>
          )}

          {activeView === 'local' && (
            <div className="space-y-1">
              {taxTables.local?.map(table => (
                <button
                  key={table.id}
                  onClick={() => fetchTableDetails(table.jurisdiction_code, table.tax_type)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    selectedTable?.table?.id === table.id ? 'border-sky-500 bg-sky-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm">{table.jurisdiction_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Parent: {table.parent_jurisdiction}</span>
                    {getStatusBadge(table.verification_status)}
                  </div>
                </button>
              ))}
            </div>
          )}

          {activeView === 'audit' && (
            <div className="space-y-1">
              {auditLog.length === 0 ? (
                <p className="text-sm text-gray-500 p-4 text-center">No audit entries yet</p>
              ) : (
                auditLog.map((entry, idx) => (
                  <div key={idx} className="p-3 border rounded-lg text-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        entry.action === 'verified' ? 'bg-emerald-100 text-emerald-700' :
                        entry.action === 'updated' ? 'bg-amber-100 text-amber-700' :
                        entry.action === 'rollback' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>{entry.action}</span>
                      <span className="text-gray-600">{entry.jurisdiction_code}</span>
                    </div>
                    <p className="text-xs text-gray-500">By {entry.changed_by} • {new Date(entry.changed_at).toLocaleString()}</p>
                    {entry.reason && <p className="text-xs text-gray-600 mt-1">{entry.reason}</p>}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Right Panel - Details */}
        <div className="col-span-2">
          {!selectedTable && activeView !== 'audit' ? (
            <Card className="h-full flex items-center justify-center">
              <CardContent className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">Select a tax table to view details</p>
              </CardContent>
            </Card>
          ) : activeView === 'audit' ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" /> Audit Trail
                </CardTitle>
                <CardDescription>Complete history of all tax table changes</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {auditLog.map((entry, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-sm font-medium ${
                            entry.action === 'verified' ? 'bg-emerald-100 text-emerald-700' :
                            entry.action === 'updated' ? 'bg-amber-100 text-amber-700' :
                            entry.action === 'rollback' ? 'bg-red-100 text-red-700' :
                            'bg-sky-100 text-sky-700'
                          }`}>{entry.action.toUpperCase()}</span>
                          <span className="font-medium">{entry.jurisdiction_code}</span>
                        </div>
                        <span className="text-sm text-gray-500">{new Date(entry.changed_at).toLocaleString()}</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p><strong>By:</strong> {entry.changed_by}</p>
                        {entry.reason && <p><strong>Reason:</strong> {entry.reason}</p>}
                        {entry.field_changed && <p><strong>Field:</strong> {entry.field_changed}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {selectedTable.table.tax_name}
                      {getStatusBadge(selectedTable.table.verification_status)}
                    </CardTitle>
                    <CardDescription>{selectedTable.table.jurisdiction_code} • {selectedTable.table.jurisdiction_type}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {!editMode && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => fetchHistory(selectedTable.table.jurisdiction_code, selectedTable.table.tax_type)}>
                          <History className="w-4 h-4 mr-1" /> History
                        </Button>
                        <Button variant="outline" size="sm" onClick={startEdit}>
                          <Edit3 className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        {selectedTable.table.verification_status !== 'verified' && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleVerify}>
                            <Check className="w-4 h-4 mr-1" /> Mark as Verified
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Source Information */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                    <Link2 className="w-4 h-4" /> Official Source
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Authority:</strong> {selectedTable.table.source_name}</p>
                    <p className="flex items-center gap-2">
                      <strong>Source URL:</strong> 
                      <a href={selectedTable.table.source_url} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline flex items-center gap-1">
                        {selectedTable.table.source_url?.slice(0, 50)}... <ExternalLink className="w-3 h-3" />
                      </a>
                    </p>
                    {selectedTable.table.last_verified && (
                      <p className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <strong>Last Verified:</strong> {new Date(selectedTable.table.last_verified).toLocaleString()}
                        {selectedTable.table.verified_by && <span className="text-gray-500">by {selectedTable.table.verified_by}</span>}
                      </p>
                    )}
                  </div>
                </div>

                {/* Version Info */}
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Current Version
                  </h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Version</p>
                      <p className="font-medium">{selectedTable.table.current_version?.version_number || 1}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Effective Date</p>
                      <p className="font-medium">{selectedTable.table.current_version?.effective_date || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Last Updated</p>
                      <p className="font-medium">{new Date(selectedTable.table.updated_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  {selectedTable.table.current_version?.notes && (
                    <div className="mt-3 p-2 bg-amber-50 rounded text-sm text-amber-800">
                      <strong>Note:</strong> {selectedTable.table.current_version.notes}
                    </div>
                  )}
                </div>

                {/* Tax Rates - View Mode */}
                {!editMode && (
                  <div>
                    <h4 className="font-medium text-sm mb-3">Tax Rates</h4>
                    
                    {/* Brackets */}
                    {selectedTable.table.current_version?.brackets?.length > 0 && (
                      <table className="w-full text-sm border rounded-lg overflow-hidden">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="text-left p-3">Min Amount</th>
                            <th className="text-left p-3">Max Amount</th>
                            <th className="text-left p-3">Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedTable.table.current_version.brackets.map((bracket, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="p-3">${bracket.min_amount?.toLocaleString()}</td>
                              <td className="p-3">{bracket.max_amount ? `$${bracket.max_amount.toLocaleString()}` : 'No limit'}</td>
                              <td className="p-3 font-medium">{(bracket.rate * 100).toFixed(2)}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {/* Additional Rates */}
                    {selectedTable.table.current_version?.additional_rates && Object.keys(selectedTable.table.current_version.additional_rates).length > 0 && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Additional Information</h5>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {Object.entries(selectedTable.table.current_version.additional_rates).map(([key, value]) => (
                            value !== null && value !== undefined && (
                              <div key={key} className="flex justify-between">
                                <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}:</span>
                                <span className="font-medium">
                                  {typeof value === 'number' && key.includes('rate') ? `${(value * 100).toFixed(3)}%` : 
                                   typeof value === 'number' ? `$${value.toLocaleString()}` : 
                                   String(value)}
                                </span>
                              </div>
                            )
                          ))}
                        </div>
                      </div>
                    )}

                    {/* No brackets message */}
                    {(!selectedTable.table.current_version?.brackets || selectedTable.table.current_version.brackets.length === 0) && 
                     selectedTable.table.current_version?.additional_rates?.type === 'none' && (
                      <div className="p-4 bg-emerald-50 text-emerald-700 rounded-lg text-center">
                        <p className="font-medium">No State Income Tax</p>
                        <p className="text-sm">This jurisdiction does not levy a state income tax.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Tax Rates - Edit Mode */}
                {editMode && editData && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Edit Tax Rates</h4>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setEditMode(false); setEditData(null); setEditReason(''); }}>
                          <X className="w-4 h-4 mr-1" /> Cancel
                        </Button>
                        <Button size="sm" onClick={handleSaveChanges} disabled={saving}>
                          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                          Save Changes
                        </Button>
                      </div>
                    </div>

                    {/* Effective Date */}
                    <div>
                      <label className="text-sm font-medium">Effective Date</label>
                      <Input 
                        type="date" 
                        value={editData.effective_date} 
                        onChange={(e) => setEditData({...editData, effective_date: e.target.value})}
                        className="w-48 mt-1"
                      />
                    </div>

                    {/* Brackets Editor */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium">Tax Brackets</label>
                        <Button variant="outline" size="sm" onClick={addBracket}>
                          <Plus className="w-4 h-4 mr-1" /> Add Bracket
                        </Button>
                      </div>
                      <table className="w-full text-sm border rounded-lg overflow-hidden">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="text-left p-2">Min Amount ($)</th>
                            <th className="text-left p-2">Max Amount ($)</th>
                            <th className="text-left p-2">Rate (%)</th>
                            <th className="w-10"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {editData.brackets.map((bracket, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="p-2">
                                <Input 
                                  type="number" 
                                  value={bracket.min_amount || 0}
                                  onChange={(e) => updateBracket(idx, 'min_amount', e.target.value)}
                                  className="h-8"
                                />
                              </td>
                              <td className="p-2">
                                <Input 
                                  type="number" 
                                  value={bracket.max_amount || ''}
                                  placeholder="No limit"
                                  onChange={(e) => updateBracket(idx, 'max_amount', e.target.value || null)}
                                  className="h-8"
                                />
                              </td>
                              <td className="p-2">
                                <Input 
                                  type="number" 
                                  step="0.001"
                                  value={(bracket.rate * 100).toFixed(3)}
                                  onChange={(e) => updateBracket(idx, 'rate', parseFloat(e.target.value) / 100)}
                                  className="h-8"
                                />
                              </td>
                              <td className="p-2">
                                <Button variant="ghost" size="sm" onClick={() => removeBracket(idx)}>
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Change Reason (Required) */}
                    <div>
                      <label className="text-sm font-medium text-red-600">* Reason for Change (Required)</label>
                      <Input 
                        value={editReason}
                        onChange={(e) => setEditReason(e.target.value)}
                        placeholder="Explain why this change is being made..."
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">This will be recorded in the audit trail.</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* History Modal */}
      {showHistory && historyData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-[800px] max-h-[80vh] overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" /> Version History
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowHistory(false)}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                {/* Current Version */}
                <div className="p-4 border-2 border-sky-500 rounded-lg bg-sky-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">Version {historyData.current_version?.version_number} (Current)</span>
                    <span className="text-sm text-gray-500">Effective: {historyData.current_version?.effective_date}</span>
                  </div>
                  <p className="text-sm text-gray-600">{historyData.current_version?.notes || 'No notes'}</p>
                  <p className="text-xs text-gray-400 mt-2">Created by {historyData.current_version?.created_by}</p>
                </div>

                {/* Historical Versions */}
                {historyData.version_history?.map((version, idx) => (
                  <div key={idx} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Version {version.version_number}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">Effective: {version.effective_date}</span>
                        {version.is_locked && <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">Locked</span>}
                        <Button variant="outline" size="sm" onClick={() => handleRollback(version.version_id)}>
                          <RotateCcw className="w-4 h-4 mr-1" /> Rollback
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">{version.notes || 'No notes'}</p>
                    <p className="text-xs text-gray-400 mt-2">Created by {version.created_by}</p>
                  </div>
                ))}

                {(!historyData.version_history || historyData.version_history.length === 0) && (
                  <p className="text-center text-gray-500 py-4">No previous versions available</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TaxTableManager;
